
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listsApi } from '../utils/listsApi';
import './Lists.css';

const MAX_LISTS = 10;

function ListsPage() {
  const navigate = useNavigate();

  const [lists, setLists] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }

    let active = true;

    async function loadLists() {
      try {
        const data = await listsApi('/lists');

        if (active) {
          setLists(data);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadLists();

    return () => {
      active = false;
    };
  }, [navigate]);

  // Create a new list
  async function handleCreate(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const newList = await listsApi('/lists', {
        method: 'POST',
        body: {
          name: name.trim(),
          description
        }
      });

      setLists(previous => [
        { ...newList, animal_count: 0 },
        ...previous
      ]);

      setName('');
      setDescription('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Delete a list
  async function handleDelete(list) {
    const confirmed = window.confirm(
      `Delete "${list.name}"?\n\n` +
      'The list and its entries will be removed. ' +
      'The animals themselves will not be deleted.'
    );

    if (!confirmed) return;

    setError('');

    try {
      await listsApi(`/lists/${list.id}`, {
        method: 'DELETE'
      });

      setLists(previous =>
        previous.filter(item => item.id !== list.id)
      );
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <main className="lists-page">

      <div className="lists-header">
        <div>
          <h1>My Lists</h1>
          <p>Organize animals into your own collections.</p>
        </div>

        <span className="lists-counter">
          {lists.length} / {MAX_LISTS} custom lists
        </span>
      </div>

      {error && (
        <p className="lists-error" role="alert">
          {error}
        </p>
      )}

      <div className="lists-grid">

        {/* Permanent Favorites collection */}
        <article className="list-card">
          <Link to="/favorites" className="list-card-link">
            <h2>♥ Favorites</h2>
            <p>Your permanent favorites collection.</p>
          </Link>

          <div className="list-card-footer">
            <span>Permanent collection</span>
          </div>
        </article>

        {/* Custom lists */}
        {lists.map(list => (
          <article className="list-card" key={list.id}>

            <Link
              to={`/lists/${list.id}`}
              className="list-card-link"
            >
              <h2>{list.name}</h2>

              <p>
                {list.description || 'No description yet.'}
              </p>
            </Link>

            <div className="list-card-footer">
              <span>
                {list.animal_count ?? 0} animals
              </span>

              <button
                type="button"
                className="list-delete-btn"
                onClick={() => handleDelete(list)}
                aria-label={`Delete ${list.name}`}
                title="Delete list"
              >
                🗑
              </button>
            </div>

          </article>
        ))}
      </div>

      {loading && <p>Loading your lists...</p>}

      {!loading && lists.length === 0 && (
        <p>You haven't created any custom lists yet.</p>
      )}

      {/* Create a list */}
      <section className="lists-create">
        <h2>Create a New List</h2>

        {lists.length >= MAX_LISTS ? (
          <p>
            You've reached the limit of {MAX_LISTS}
            {' '}custom lists.
          </p>
        ) : (
          <form onSubmit={handleCreate}>

            <label htmlFor="new-list-name">
              List name
            </label>

            <input
              id="new-list-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Example: Arctic Wildlife"
              maxLength={18}
              required
            />

            <label htmlFor="new-list-description">
              Description
            </label>

            <textarea
              id="new-list-description"
              value={description}
              onChange={e =>
                setDescription(e.target.value)
              }
              placeholder="What is this list for?"
              maxLength={200}
              rows={3}
            />

            <button
              className="list-primary-btn"
              type="submit"
              disabled={saving || !name.trim()}
            >
              {saving ? 'Creating...' : '+ Create List'}
            </button>

          </form>
        )}
      </section>

    </main>
  );
}

export default ListsPage;
